import { useEffect, useRef, useState } from 'react'
import type { Pallet, PressureFrame, PressureMarker } from '../types/domain'
import { formatPressure, type PressureUnit } from './pressureUnits'

export function Pressure3D({ pallet, frame, unit }: { pallet: Pallet; frame: PressureFrame; unit: PressureUnit }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const dragRef = useRef<{ pointerId: number; x: number; y: number } | undefined>(undefined)
  const [view, setView] = useState({ yaw: -42, pitch: 25 })
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const resize = () => draw(canvas, pallet, frame, view)
    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(canvas)
    return () => observer.disconnect()
  }, [pallet, frame, view])

  function rotate(deltaYaw: number, deltaPitch: number) {
    setView(current => ({
      yaw: current.yaw + deltaYaw,
      pitch: Math.max(8, Math.min(72, current.pitch + deltaPitch)),
    }))
  }

  return (
    <div className="pressure-layout">
      <div className="pressure-canvas">
        <canvas ref={canvasRef} tabIndex={0} role="img"
          aria-label={`Интерактивная трёхмерная схема нагрузки паллеты ${pallet.number}. Перетаскивайте для вращения или используйте клавиши со стрелками.`}
          onPointerDown={event => {
            event.currentTarget.setPointerCapture(event.pointerId)
            dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY }
          }}
          onPointerMove={event => {
            const previous = dragRef.current
            if (!previous || previous.pointerId !== event.pointerId) return
            rotate((event.clientX - previous.x) * .45, -(event.clientY - previous.y) * .35)
            dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY }
          }}
          onPointerUp={event => {
            if (dragRef.current?.pointerId === event.pointerId) dragRef.current = undefined
            event.currentTarget.releasePointerCapture(event.pointerId)
          }}
          onPointerCancel={() => { dragRef.current = undefined }}
          onKeyDown={event => {
            if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return
            event.preventDefault()
            rotate(event.key === 'ArrowLeft' ? -8 : event.key === 'ArrowRight' ? 8 : 0, event.key === 'ArrowUp' ? 5 : event.key === 'ArrowDown' ? -5 : 0)
          }} />
        <div className="pressure-direction"><i aria-hidden="true">→</i><span><strong>Направление давления</strong><small>от плёнки к центру груза</small></span></div>
        <div className="pressure-rotate-hint"><span aria-hidden="true">↻</span> Тяните модель для вращения</div>
        <div className="pressure-controls" aria-label="Управление поворотом модели">
          <button type="button" aria-label="Повернуть влево" onClick={() => rotate(-12, 0)}>←</button>
          <button type="button" aria-label="Повернуть вправо" onClick={() => rotate(12, 0)}>→</button>
          <button type="button" aria-label="Поднять точку обзора" onClick={() => rotate(0, 8)}>↑</button>
          <button type="button" aria-label="Опустить точку обзора" onClick={() => rotate(0, -8)}>↓</button>
          <button type="button" className="pressure-reset" onClick={() => setView({ yaw: -42, pitch: 25 })}>Сбросить</button>
        </div>
        <div className="pressure-scale"><span>{formatPressure(frame.minKpa, unit)}</span><i /><span>{formatPressure(frame.maxKpa, unit)}</span></div>
      </div>
      <div className="marker-table" aria-label="Исходные значения датчиков">
        {frame.markers.map(marker => (
          <div key={marker.sensorId}>
            <span><i style={{ background: colorFor(marker.valueKpa, frame.minKpa, frame.maxKpa) }} />{marker.sensorSerial}<small>{faceLabel(marker)}</small></span>
            <strong>{marker.valueKpa === undefined ? 'нет данных' : formatPressure(marker.valueKpa, unit)}</strong>
          </div>
        ))}
      </div>
    </div>
  )
}

function draw(canvas: HTMLCanvasElement, pallet: Pallet, frame: PressureFrame, view: { yaw: number; pitch: number }) {
  const ratio = window.devicePixelRatio || 1
  const width = Math.max(280, canvas.clientWidth)
  const height = Math.max(280, canvas.clientHeight)
  canvas.width = width * ratio
  canvas.height = height * ratio
  const context = canvas.getContext('2d')
  if (!context) return
  context.scale(ratio, ratio)
  context.clearRect(0, 0, width, height)

  const yaw = view.yaw * Math.PI / 180
  const pitch = view.pitch * Math.PI / 180
  const compact = width < 440
  const scale = Math.min(width * (compact ? .5 : .56), height * (compact ? .49 : .54))
  const transform = (x: number, y: number, z: number) => {
    const nx = x / pallet.widthMm - .5
    const ny = y / pallet.depthMm - .5
    const nz = (z / pallet.heightMm - .5) * Math.min(1.35, pallet.heightMm / Math.max(pallet.widthMm, pallet.depthMm))
    const rx = nx * Math.cos(yaw) - ny * Math.sin(yaw)
    const ry = nx * Math.sin(yaw) + ny * Math.cos(yaw)
    return { rx, ry, nz, depth: ry * Math.cos(pitch) + nz * Math.sin(pitch) }
  }
  const project = (x: number, y: number, z: number) => {
    const point = transform(x, y, z)
    return {
      x: width * .5 + point.rx * scale,
      y: height * (compact ? .52 : .49) + (point.ry * Math.sin(pitch) - point.nz * Math.cos(pitch)) * scale,
      depth: point.depth,
    }
  }
  const vertices = [
    [0, 0, 0], [pallet.widthMm, 0, 0], [pallet.widthMm, pallet.depthMm, 0], [0, pallet.depthMm, 0],
    [0, 0, pallet.heightMm], [pallet.widthMm, 0, pallet.heightMm], [pallet.widthMm, pallet.depthMm, pallet.heightMm], [0, pallet.depthMm, pallet.heightMm],
  ].map(([x, y, z]) => project(x ?? 0, y ?? 0, z ?? 0))
  const faces = [
    { indices: [0, 1, 5, 4], color: '#dce7f0' }, { indices: [1, 2, 6, 5], color: '#e5edf4' },
    { indices: [2, 3, 7, 6], color: '#d8e3ec' }, { indices: [3, 0, 4, 7], color: '#e2ebf2' },
    { indices: [4, 5, 6, 7], color: '#eef3f8' }, { indices: [0, 3, 2, 1], color: '#cbd9e5' },
  ].sort((left, right) => averageDepth(left.indices, vertices) - averageDepth(right.indices, vertices))
  context.lineJoin = 'round'
  faces.forEach(({ indices, color }) => {
    context.beginPath()
    indices.forEach((vertexIndex, pointIndex) => {
      const point = vertices[vertexIndex]
      if (!point) return
      if (pointIndex === 0) context.moveTo(point.x, point.y)
      else context.lineTo(point.x, point.y)
    })
    context.closePath()
    context.fillStyle = color
    context.fill()
    context.strokeStyle = '#829ab1'
    context.lineWidth = 1.5
    context.stroke()
  })
  const edges: Array<readonly [number, number]> = [[0, 1], [1, 2], [2, 3], [3, 0], [4, 5], [5, 6], [6, 7], [7, 4], [0, 4], [1, 5], [2, 6], [3, 7]]
  edges.forEach(([a, b]) => {
    const from = vertices[a]
    const to = vertices[b]
    if (!from || !to) return
    context.beginPath(); context.moveTo(from.x, from.y); context.lineTo(to.x, to.y)
    context.strokeStyle = '#829ab1'; context.lineWidth = 1.2; context.stroke()
  })

  context.fillStyle = '#52687c'
  context.font = '700 10px Inter, system-ui, sans-serif'
  const front = project(pallet.widthMm / 2, 0, 0)
  context.fillText('ПЕРЕДНЯЯ СТОРОНА', front.x - 52, front.y + 28)

  for (let wrap = 1; wrap <= 4; wrap++) {
    const z = pallet.heightMm * wrap / 5
    const left = project(0, 0, z)
    const right = project(pallet.widthMm, 0, z)
    const rear = project(pallet.widthMm, pallet.depthMm, z)
    context.beginPath(); context.moveTo(left.x, left.y); context.lineTo(right.x, right.y); context.lineTo(rear.x, rear.y)
    context.strokeStyle = 'rgba(47, 128, 237, .16)'; context.lineWidth = 2; context.stroke()
  }

  frame.markers.forEach(marker => {
    const origin = project(marker.xMm, marker.yMm, marker.zMm)
    const center = project(pallet.widthMm / 2, pallet.depthMm / 2, marker.zMm)
    const norm = normalize(marker.valueKpa, frame.minKpa, frame.maxKpa)
    const distance = Math.hypot(center.x - origin.x, center.y - origin.y) || 1
    const direction = { x: (center.x - origin.x) / distance, y: (center.y - origin.y) / distance }
    const length = marker.valueKpa === undefined ? 12 : 26 + norm * 50
    const tail = { x: origin.x - direction.x * 26, y: origin.y - direction.y * 26 }
    const end = { x: origin.x + direction.x * length, y: origin.y + direction.y * length }
    const color = colorFor(marker.valueKpa, frame.minKpa, frame.maxKpa)
    context.beginPath(); context.moveTo(tail.x, tail.y); context.lineTo(end.x, end.y)
    context.strokeStyle = color; context.lineWidth = 6; context.lineCap = 'round'; context.stroke()
    drawArrowHead(context, end.x, end.y, direction.x, direction.y, color)
    context.beginPath(); context.rect(origin.x - 7, origin.y - 7, 14, 14)
    context.fillStyle = '#ffffff'; context.fill(); context.strokeStyle = color; context.lineWidth = 3; context.stroke()
    context.fillStyle = '#102a43'; context.font = `${width < 440 ? '600 9px' : '600 11px'} Inter, system-ui, sans-serif`
    const sensorLabel = marker.sensorSerial.replace('NR-S-', '#')
    const labelWidth = context.measureText(sensorLabel).width
    const preferredX = tail.x - direction.x * 6 - 10
    const preferredY = tail.y - direction.y * 6
    const labelX = Math.max(5, Math.min(width - labelWidth - 5, preferredX))
    const labelY = Math.max(18, Math.min(height - 18, preferredY))
    context.fillText(sensorLabel, labelX, labelY)
  })
}

function averageDepth(indices: number[], vertices: Array<{ depth: number }>) {
  return indices.reduce((sum, index) => sum + (vertices[index]?.depth ?? 0), 0) / indices.length
}

function drawArrowHead(context: CanvasRenderingContext2D, x: number, y: number, dx: number, dy: number, color: string) {
  const size = 10
  context.beginPath()
  context.moveTo(x, y)
  context.lineTo(x - dx * size - dy * size * .55, y - dy * size + dx * size * .55)
  context.lineTo(x - dx * size + dy * size * .55, y - dy * size - dx * size * .55)
  context.closePath()
  context.fillStyle = color
  context.fill()
}

function normalize(value: number | undefined, min: number, max: number) {
  return value === undefined ? 0 : (value - min) / Math.max(0.001, max - min)
}

function colorFor(value: number | undefined, min: number, max: number): string {
  if (value === undefined) return '#94a3b8'
  const t = Math.max(0, Math.min(1, normalize(value, min, max)))
  if (t < 0.5) return mix('#2f80ed', '#e9b949', t * 2)
  return mix('#e9b949', '#d64545', (t - 0.5) * 2)
}

function mix(from: string, to: string, value: number) {
  const a = from.match(/\w\w/g)?.map(hex => Number.parseInt(hex, 16)) ?? [0, 0, 0]
  const b = to.match(/\w\w/g)?.map(hex => Number.parseInt(hex, 16)) ?? [0, 0, 0]
  return `rgb(${a.map((channel, index) => Math.round(channel + ((b[index] ?? channel) - channel) * value)).join(',')})`
}

function faceLabel(marker: PressureMarker) {
  const corners = { FRONT: 'передний левый угол', RIGHT: 'передний правый угол', BACK: 'задний правый угол', LEFT: 'задний левый угол' }
  return `${corners[marker.face]} · ${marker.level === 'TOP' ? 'верх' : 'низ'}`
}
